import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { adminApi } from '@/api';
import {
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  ListCard,
  LoadingState,
  Pagination,
  SearchInput,
  SegmentedTabs,
  Select,
  ViewButton,
} from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate } from '@/utils/format';

import { studentSubscriptionPath } from './links';
import { formatMoney, formatPrice, statusLabel, statusTone } from './money';

/**
 * The student subscriber roster.
 *
 * A student on Free has no subscription row, and neither does one whose only
 * request is an unpaid invoice — the student's own record is where those are
 * managed. A row whose period has run out is labelled Lapsed even before the
 * renewal job has caught up with it, because that is what the student gets.
 */
const STATUS_TABS = [
  { key: '', label: 'All' },
  { key: 'ACTIVE', label: 'Active' },
  { key: 'PAST_DUE', label: 'Payment overdue' },
  { key: 'CANCELED', label: 'Ended' },
];

const PAGE_SIZE = 20;

export function StudentSubscriptionsList({ planId, onPlanChange }) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [ending, setEnding] = useState('');
  const [page, setPage] = useState(1);

  const params = {
    page,
    page_size: PAGE_SIZE,
    ...(status ? { status } : {}),
    ...(planId ? { plan_id: planId } : {}),
    ...(ending ? { ending: ending === 'yes' } : {}),
    ...(search.trim() ? { q: search.trim() } : {}),
  };

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['admin-student-subscriptions', params],
    queryFn: () => adminApi.listStudentSubscriptions(params),
    placeholderData: keepPreviousData,
  });
  const plans = useQuery({ queryKey: ['admin-plans'], queryFn: adminApi.listBillingPlans });
  const studentPlans = (plans.data ?? []).filter((plan) => plan.audience === 'STUDENT');

  const activeCount = [search !== '', status !== '', planId !== '', ending !== ''].filter(
    Boolean,
  ).length;

  function resetFilters() {
    setSearch('');
    setStatus('');
    setEnding('');
    if (planId) onPlanChange('');
    setPage(1);
  }

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <ListCard
      isRefreshing={isPlaceholderData}
      toolbarStart={
        <SegmentedTabs
          variant="plain"
          label="Status"
          tabs={STATUS_TABS}
          value={status}
          onChange={(key) => {
            setStatus(key);
            setPage(1);
          }}
        />
      }
      toolbarEnd={
        <>
          <Select
            label="Plan"
            hideLabel
            size="sm"
            wrapperClassName="w-full sm:w-44"
            value={planId}
            onChange={(event) => {
              onPlanChange(event.target.value);
              setPage(1);
            }}
          >
            <option value="">All student plans</option>
            {studentPlans.map((plan) => (
              <option key={plan.code} value={plan.id}>
                {plan.name}
              </option>
            ))}
          </Select>
          <Select
            label="Ending"
            hideLabel
            size="sm"
            wrapperClassName="w-full sm:w-44"
            value={ending}
            onChange={(event) => {
              setEnding(event.target.value);
              setPage(1);
            }}
          >
            <option value="">All subscriptions</option>
            <option value="yes">Ending</option>
            <option value="no">Continuing</option>
          </Select>
          <SearchInput
            label="Search student subscriptions"
            hideLabel
            size="sm"
            className="w-full sm:w-64"
            placeholder="Student name or email"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            onClear={() => {
              setSearch('');
              setPage(1);
            }}
          />
          {activeCount > 0 && (
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              <Icon name="reset" size={14} />
              Reset
            </Button>
          )}
        </>
      }
      state={
        isError ? (
          <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
        ) : isPending ? (
          <LoadingState label="Loading student subscriptions…" />
        ) : rows.length === 0 ? (
          activeCount > 0 ? (
            <EmptyState
              title="No student subscriptions"
              description="Nothing matches these filters."
              action={
                <Button variant="secondary" size="sm" onClick={resetFilters}>
                  Reset filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No student subscriptions yet"
              description="A student appears here once a paid plan has started. Assign one from the student's record."
            />
          )
        ) : null
      }
      footer={
        meta && (
          <Pagination
            className=""
            page={meta.page}
            pageSize={PAGE_SIZE}
            totalPages={meta.total_pages}
            totalItems={meta.total_items}
            onPageChange={setPage}
          />
        )
      }
    >
      <BoxList>
        {rows.map((row) => {
          const lapsed = !row.is_running && row.status !== 'CANCELED';
          const tone = lapsed ? 'warning' : statusTone(row.status);
          return (
            <BoxRow
              key={row.id}
              accent={tone}
              leading={<BoxAvatar name={row.student_name} round />}
              title={row.student_name}
              to={studentSubscriptionPath(row.id)}
              badges={
                <>
                  <Badge dot tone={tone}>
                    {lapsed ? 'Lapsed' : statusLabel(row.status)}
                  </Badge>
                  {row.cancel_at_period_end && row.is_running && (
                    <Badge tone="warning">Ending</Badge>
                  )}
                </>
              }
              subtitle={
                <Link to={paths.admin.studentDetail(row.student_id)} className="hover:underline">
                  {row.student_email}
                </Link>
              }
              facts={[
                {
                  icon: 'package',
                  tone: 'brand',
                  text: `${row.plan_name} · ${formatPrice(row.price_cents, row.currency, row.interval)}`,
                },
                {
                  icon: 'calendar',
                  text: `${row.cancel_at_period_end || !row.is_running ? 'Ends' : 'Renews'} ${formatDate(row.current_period_end)}`,
                },
                row.outstanding_cents > 0
                  ? {
                      icon: 'banknote',
                      tone: 'warning',
                      text: `${formatMoney(row.outstanding_cents, row.currency)} outstanding · ${row.open_invoices} open`,
                    }
                  : { icon: 'banknote', text: 'Nothing outstanding' },
              ]}
              meta={
                <span className="text-ink-muted text-xs">
                  Subscribed {formatDate(row.created_at)}
                </span>
              }
              actions={
                <ViewButton
                  to={studentSubscriptionPath(row.id)}
                  title={`Manage ${row.student_name}'s plan`}
                  label="Manage plan"
                  icon="settings"
                />
              }
            />
          );
        })}
      </BoxList>
    </ListCard>
  );
}
